// modules/website/components/FishingCalendarComponent.tsx - Calendário de Pesca por Espécie

import React from 'react';
import { FishingCalendarData } from '@/types';
import { Calendar, Fish } from 'lucide-react';

interface Props {
  data: FishingCalendarData;
  settings?: any;
}

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export const FishingCalendarComponent: React.FC<Props> = ({ data, settings }) => {
  const {
    title = 'Calendário de Pesca',
    subtitle,
    species = [],
    showLegend = true,
    highlightCurrentMonth = true
  } = data || {};

  const currentMonth = new Date().getMonth() + 1; // 1-12

  // Cores de dificuldade
  const difficultyColors = {
    'fácil': 'text-green-600',
    'médio': 'text-yellow-600',
    'difícil': 'text-red-600'
  };

  const difficultyBg = {
    'fácil': 'bg-green-100',
    'médio': 'bg-yellow-100',
    'difícil': 'bg-red-100'
  };

  return (
    <div 
      className="py-16 px-6"
      style={{ backgroundColor: settings?.backgroundColor || '#ffffff' }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-4">
            <Calendar className="text-blue-600" size={36} />
            <h2 className="text-4xl font-bold text-gray-900">{title}</h2>
          </div>
          {subtitle && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{subtitle}</p>
          )}
        </div>

        {/* Legenda */}
        {showLegend && (
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-4 bg-blue-500 rounded" />
              <span className="text-sm text-gray-600">Boa época</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-4 bg-blue-700 rounded" />
              <span className="text-sm text-gray-600">Época de pico</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-4 bg-gray-200 rounded" />
              <span className="text-sm text-gray-600">Fora de temporada</span>
            </div>
          </div>
        )}

        {/* Tabela do Calendário */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[800px]">
            {/* Header com meses */}
            <thead>
              <tr>
                <th className="p-3 text-left font-semibold text-gray-700 bg-gray-50 rounded-tl-lg sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  Espécie
                </th>
                {MONTHS.map((month, idx) => (
                  <th 
                    key={month}
                    className={`p-3 text-center font-semibold min-w-[60px] ${
                      highlightCurrentMonth && (idx + 1) === currentMonth
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-50 text-gray-700'
                    } ${idx === 11 ? 'rounded-tr-lg' : ''}`}
                  >
                    {month}
                  </th>
                ))}
                <th className="p-3 text-center font-semibold text-gray-700 bg-gray-50">
                  Dificuldade
                </th>
              </tr>
            </thead>

            {/* Linhas de espécies */}
            <tbody>
              {species.map((fish, fishIdx) => (
                <tr 
                  key={fish.name}
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    fishIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                  }`}
                >
                  {/* Nome da espécie */}
                  <td className="p-3 sticky left-0 bg-inherit shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] z-10">
                    <div className="flex items-center gap-3">
                      {fish.image ? (
                        <img 
                          src={fish.image} 
                          alt={fish.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Fish className="text-blue-600" size={20} />
                        </div>
                      )}
                      <span className="font-medium text-gray-900 whitespace-nowrap">{fish.name}</span>
                    </div>
                  </td>

                  {/* Meses */}
                  {MONTHS.map((_, monthIdx) => {
                    const month = monthIdx + 1;
                    const isBestMonth = fish.bestMonths?.includes(month);
                    const isPeakMonth = fish.peakMonths?.includes(month);
                    const isCurrentMonth = highlightCurrentMonth && month === currentMonth;

                    return (
                      <td 
                        key={monthIdx}
                        className={`p-3 text-center ${isCurrentMonth ? 'ring-2 ring-blue-400 ring-inset' : ''}`}
                      >
                        {isPeakMonth ? (
                          <div className="w-full h-6 bg-blue-700 rounded flex items-center justify-center" title="Época de Pico">
                            <span className="text-white text-xs font-bold">🎣</span>
                          </div>
                        ) : isBestMonth ? (
                          <div className="w-full h-6 bg-blue-500 rounded flex items-center justify-center" title="Boa Época">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        ) : (
                          <div className="w-full h-6 bg-gray-200 rounded" />
                        )}
                      </td>
                    );
                  })}

                  {/* Dificuldade */}
                  <td className="p-3 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${difficultyBg[fish.difficulty]} ${difficultyColors[fish.difficulty]}`}>
                      {fish.difficulty}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {species.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <Fish size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">Nenhuma espécie cadastrada</p>
            <p className="text-sm">Configure as espécies e seus meses de temporada</p>
          </div>
        )}

        {/* Nota sobre mês atual */}
        {highlightCurrentMonth && species.length > 0 && (
          <div className="mt-6 text-center text-sm text-gray-500">
            <span className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full">
              <Calendar size={16} className="text-blue-600" />
              Mês atual destacado: <strong>{MONTHS[currentMonth - 1]}</strong>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
